import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';

export const MinistryCompliance = () => {
  const { data } = useApp();
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Comprehensive ministry compliance data
  const ministryComplianceData = [
    {
      id: 'MWT',
      name: 'Ministry of Works & Transport',
      permanentSecretary: 'Mr. James Ithete',
      minister: 'Hon. John Mutorwa',
      overallScore: 58,
      status: 'At Risk',
      complianceAreas: {
        procurement: {
          score: 45,
          status: 'Critical',
          issues: [
            'Annual Procurement Plan not submitted (90 days overdue)',
            'Award publication compliance: 64% (target 100%)',
            '8 non-compliance notices issued by PPU',
            'Funds certification delays averaging 12 days'
          ],
          kpis: [
            { metric: 'Annual Plan Submitted', target: 'Yes', actual: 'No', status: 'Critical' },
            { metric: 'Award Publication (7 days)', target: 100, actual: 64, unit: '%', status: 'Critical' },
            { metric: 'PPU Non-Compliance Notices', target: 0, actual: 8, unit: 'notices', status: 'Critical' },
            { metric: 'Funds Certification (14 days)', target: 100, actual: 72, unit: '%', status: 'At Risk' }
          ],
          lastAudit: '2024-12-15',
          nextDeadline: '2025-02-01',
          responsibleOfficer: 'Chief Procurement Officer - T. Shivute'
        },
        ndp6Reporting: {
          score: 62,
          status: 'At Risk',
          issues: [
            'Q4 2024 report submitted 8 days late',
            'Pillar 1 (Economic Advancement) progress: 58% vs target 65%',
            'Budget execution at 58% (below 65% threshold)',
            '3 projects flagged as "At Risk" or "Delayed"'
          ],
          kpis: [
            { metric: 'Quarterly Reports On Time', target: 100, actual: 75, unit: '%', status: 'At Risk' },
            { metric: 'NDP6 Pillar Progress', target: 65, actual: 58, unit: '%', status: 'At Risk' },
            { metric: 'Budget Execution', target: 65, actual: 58, unit: '%', status: 'At Risk' },
            { metric: 'Projects On Track', target: 90, actual: 68, unit: '%', status: 'At Risk' }
          ],
          lastReport: '2024-12-28',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of Planning - M. Nambahu'
        },
        auditCompliance: {
          score: 52,
          status: 'Critical',
          issues: [
            '18 outstanding AG recommendations from FY 2022/23',
            'Implementation rate: 52% (target 80%)',
            'Financial irregularities totaling N$4.2M unresolved',
            'Quarterly audit committee meetings not held'
          ],
          kpis: [
            { metric: 'AG Recommendations Implemented', target: 80, actual: 52, unit: '%', status: 'Critical' },
            { metric: 'Outstanding Queries', target: 0, actual: 18, unit: 'items', status: 'Critical' },
            { metric: 'Financial Irregularities Resolved', target: 100, actual: 35, unit: '%', status: 'Critical' },
            { metric: 'Audit Committee Meetings', target: 4, actual: 2, unit: 'meetings', status: 'At Risk' }
          ],
          lastAudit: '2024-11-30',
          nextDeadline: '2025-02-15',
          responsibleOfficer: 'Chief Internal Auditor - S. Kamati'
        },
        budgetExecution: {
          score: 58,
          status: 'At Risk',
          issues: [
            'Budget execution at 58% (Q2 target: 65%)',
            'Capital budget underspending: N$850M',
            '4 virements pending approval for 45+ days',
            'Monthly expenditure reports submitted late'
          ],
          kpis: [
            { metric: 'Budget Execution Rate', target: 65, actual: 58, unit: '%', status: 'At Risk' },
            { metric: 'Capital Budget Execution', target: 60, actual: 42, unit: '%', status: 'Critical' },
            { metric: 'Virements Processed (30 days)', target: 100, actual: 68, unit: '%', status: 'At Risk' },
            { metric: 'Monthly Reports On Time', target: 100, actual: 75, unit: '%', status: 'At Risk' }
          ],
          lastReport: '2025-01-10',
          nextDeadline: '2025-02-10',
          responsibleOfficer: 'Chief Accountant - L. Nghidinwa'
        },
        performanceManagement: {
          score: 68,
          status: 'At Risk',
          issues: [
            'Ministerial Performance Agreement: 68% KPIs achieved',
            'Service delivery standards: 62% compliance',
            'Staff vacancy rate: 28% (target <15%)',
            'Quarterly performance reviews delayed'
          ],
          kpis: [
            { metric: 'Ministerial KPIs Achieved', target: 80, actual: 68, unit: '%', status: 'At Risk' },
            { metric: 'Service Delivery Compliance', target: 85, actual: 62, unit: '%', status: 'At Risk' },
            { metric: 'Staff Vacancy Rate', target: 15, actual: 28, unit: '%', status: 'Critical' },
            { metric: 'Performance Reviews On Time', target: 100, actual: 75, unit: '%', status: 'At Risk' }
          ],
          lastReview: '2024-12-31',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of HR - P. Shikongo'
        },
        parliamentaryAffairs: {
          score: 72,
          status: 'On Track',
          issues: [
            '12 parliamentary questions pending (30+ days)',
            'Response rate: 72% within 30 days',
            '2 petitions unresolved (90+ days)',
            'Monthly liaison reports submitted on time'
          ],
          kpis: [
            { metric: 'Questions Answered (30 days)', target: 100, actual: 72, unit: '%', status: 'At Risk' },
            { metric: 'Pending Questions', target: 0, actual: 12, unit: 'questions', status: 'At Risk' },
            { metric: 'Petitions Resolved (90 days)', target: 100, actual: 85, unit: '%', status: 'On Track' },
            { metric: 'Monthly Liaison Reports', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReport: '2025-01-21',
          nextDeadline: '2025-02-21',
          responsibleOfficer: 'Parliamentary Liaison Officer - E. Nangolo'
        }
      },
      escalations: [
        {
          id: 'ESC-MWT-001',
          date: '2025-01-20T14:30:00Z',
          type: 'Procurement Non-Compliance',
          severity: 'Critical',
          description: 'Ministry failed to acknowledge PPU non-compliance notice within statutory 7-day period',
          status: 'Active',
          initiatedBy: 'Prime Minister\'s Office',
          assignedTo: 'Permanent Secretary'
        },
        {
          id: 'ESC-MWT-002',
          date: '2025-01-18T09:15:00Z',
          type: 'Budget Execution',
          severity: 'High',
          description: 'Capital budget execution critically low at 42% (Q2 target: 60%)',
          status: 'Under Review',
          initiatedBy: 'Ministry of Finance',
          assignedTo: 'Chief Accountant'
        },
        {
          id: 'ESC-MWT-003',
          date: '2025-01-10T11:00:00Z',
          type: 'Audit Compliance',
          severity: 'High',
          description: '18 outstanding AG recommendations from FY 2022/23 with 52% implementation rate',
          status: 'Active',
          initiatedBy: 'Office of the Auditor General',
          assignedTo: 'Chief Internal Auditor'
        }
      ],
      recentActivity: [
        {
          date: '2025-01-21T16:30:00Z',
          action: 'Parliamentary Questions Status Report submitted',
          officer: 'E. Nangolo',
          category: 'Parliamentary Affairs'
        },
        {
          date: '2025-01-20T14:30:00Z',
          action: 'Escalation raised: Procurement non-compliance',
          officer: 'Prime Minister\'s Office',
          category: 'Procurement'
        },
        {
          date: '2025-01-18T09:15:00Z',
          action: 'Budget execution alert triggered',
          officer: 'Ministry of Finance',
          category: 'Budget'
        },
        {
          date: '2025-01-10T11:00:00Z',
          action: 'Monthly budget execution report submitted (5 days late)',
          officer: 'L. Nghidinwa',
          category: 'Budget'
        },
        {
          date: '2024-12-28T15:00:00Z',
          action: 'NDP6 Q4 2024 report submitted (8 days late)',
          officer: 'M. Nambahu',
          category: 'NDP6 Reporting'
        }
      ],
      contactInfo: {
        phone: '+264 61 208 2111',
        email: 'info@mwt.gov.na',
        address: 'Private Bag 13341, Windhoek, Namibia',
        website: 'https://mwt.gov.na'
      }
    },
    {
      id: 'MOE',
      name: 'Ministry of Education, Arts & Culture',
      permanentSecretary: 'Ms. Sanet Steenkamp',
      minister: 'Hon. Anna Nghipondoka',
      overallScore: 78,
      status: 'On Track',
      complianceAreas: {
        procurement: {
          score: 82,
          status: 'On Track',
          issues: [
            'Award publication compliance: 88%',
            '2 minor non-compliance notices (resolved)',
            'Annual Procurement Plan submitted on time'
          ],
          kpis: [
            { metric: 'Annual Plan Submitted', target: 'Yes', actual: 'Yes', status: 'On Track' },
            { metric: 'Award Publication (7 days)', target: 100, actual: 88, unit: '%', status: 'On Track' },
            { metric: 'PPU Non-Compliance Notices', target: 0, actual: 0, unit: 'notices', status: 'On Track' },
            { metric: 'Funds Certification (14 days)', target: 100, actual: 92, unit: '%', status: 'On Track' }
          ],
          lastAudit: '2025-01-05',
          nextDeadline: '2025-03-01',
          responsibleOfficer: 'Chief Procurement Officer - D. Shaanika'
        },
        ndp6Reporting: {
          score: 78,
          status: 'On Track',
          issues: [
            'Q4 2024 report submitted on time',
            'Pillar 2 (Social Transformation) progress: 78%',
            'Budget execution at 78%',
            'All projects on track or at acceptable risk levels'
          ],
          kpis: [
            { metric: 'Quarterly Reports On Time', target: 100, actual: 100, unit: '%', status: 'On Track' },
            { metric: 'NDP6 Pillar Progress', target: 70, actual: 78, unit: '%', status: 'On Track' },
            { metric: 'Budget Execution', target: 70, actual: 78, unit: '%', status: 'On Track' },
            { metric: 'Projects On Track', target: 90, actual: 92, unit: '%', status: 'On Track' }
          ],
          lastReport: '2024-12-20',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of Planning - K. Haufiku'
        },
        auditCompliance: {
          score: 72,
          status: 'On Track',
          issues: [
            '6 outstanding AG recommendations from FY 2022/23',
            'Implementation rate: 72%',
            'All financial irregularities resolved',
            'Quarterly audit committee meetings held regularly'
          ],
          kpis: [
            { metric: 'AG Recommendations Implemented', target: 80, actual: 72, unit: '%', status: 'At Risk' },
            { metric: 'Outstanding Queries', target: 0, actual: 6, unit: 'items', status: 'At Risk' },
            { metric: 'Financial Irregularities Resolved', target: 100, actual: 100, unit: '%', status: 'On Track' },
            { metric: 'Audit Committee Meetings', target: 4, actual: 4, unit: 'meetings', status: 'On Track' }
          ],
          lastAudit: '2024-12-15',
          nextDeadline: '2025-03-15',
          responsibleOfficer: 'Chief Internal Auditor - R. Mukwilongo'
        },
        budgetExecution: {
          score: 78,
          status: 'On Track',
          issues: [
            'Budget execution at 78% (on track)',
            'Capital budget execution: 72%',
            'All virements processed within 30 days',
            'Monthly expenditure reports submitted on time'
          ],
          kpis: [
            { metric: 'Budget Execution Rate', target: 70, actual: 78, unit: '%', status: 'On Track' },
            { metric: 'Capital Budget Execution', target: 60, actual: 72, unit: '%', status: 'On Track' },
            { metric: 'Virements Processed (30 days)', target: 100, actual: 100, unit: '%', status: 'On Track' },
            { metric: 'Monthly Reports On Time', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReport: '2025-01-08',
          nextDeadline: '2025-02-08',
          responsibleOfficer: 'Chief Accountant - B. Amukwaya'
        },
        performanceManagement: {
          score: 82,
          status: 'On Track',
          issues: [
            'Ministerial Performance Agreement: 82% KPIs achieved',
            'Service delivery standards: 85% compliance',
            'Staff vacancy rate: 18% (target <15%)',
            'All performance reviews completed on time'
          ],
          kpis: [
            { metric: 'Ministerial KPIs Achieved', target: 80, actual: 82, unit: '%', status: 'On Track' },
            { metric: 'Service Delivery Compliance', target: 85, actual: 85, unit: '%', status: 'On Track' },
            { metric: 'Staff Vacancy Rate', target: 15, actual: 18, unit: '%', status: 'At Risk' },
            { metric: 'Performance Reviews On Time', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReview: '2024-12-31',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of HR - N. Shilongo'
        },
        parliamentaryAffairs: {
          score: 88,
          status: 'On Track',
          issues: [
            '3 parliamentary questions pending (<30 days)',
            'Response rate: 88% within 30 days',
            'All petitions resolved within 90 days',
            'Monthly liaison reports submitted on time'
          ],
          kpis: [
            { metric: 'Questions Answered (30 days)', target: 100, actual: 88, unit: '%', status: 'On Track' },
            { metric: 'Pending Questions', target: 0, actual: 3, unit: 'questions', status: 'On Track' },
            { metric: 'Petitions Resolved (90 days)', target: 100, actual: 100, unit: '%', status: 'On Track' },
            { metric: 'Monthly Liaison Reports', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReport: '2025-01-21',
          nextDeadline: '2025-02-21',
          responsibleOfficer: 'Parliamentary Liaison Officer - T. Nghaamwa'
        }
      },
      escalations: [
        {
          id: 'ESC-MOE-001',
          date: '2025-01-15T10:00:00Z',
          type: 'Audit Compliance',
          severity: 'Medium',
          description: '6 outstanding AG recommendations - implementation rate at 72% (target 80%)',
          status: 'Under Review',
          initiatedBy: 'Office of the Auditor General',
          assignedTo: 'Chief Internal Auditor'
        }
      ],
      recentActivity: [
        {
          date: '2025-01-21T16:30:00Z',
          action: 'Parliamentary Questions Status Report submitted',
          officer: 'T. Nghaamwa',
          category: 'Parliamentary Affairs'
        },
        {
          date: '2025-01-08T09:00:00Z',
          action: 'Monthly budget execution report submitted (on time)',
          officer: 'B. Amukwaya',
          category: 'Budget'
        },
        {
          date: '2024-12-31T15:00:00Z',
          action: 'Q4 2024 performance review completed',
          officer: 'N. Shilongo',
          category: 'Performance Management'
        },
        {
          date: '2024-12-20T14:00:00Z',
          action: 'NDP6 Q4 2024 report submitted (on time)',
          officer: 'K. Haufiku',
          category: 'NDP6 Reporting'
        },
        {
          date: '2024-12-15T11:00:00Z',
          action: 'Quarterly audit committee meeting held',
          officer: 'R. Mukwilongo',
          category: 'Audit Compliance'
        }
      ],
      contactInfo: {
        phone: '+264 61 293 3111',
        email: 'info@moe.gov.na',
        address: 'Private Bag 13186, Windhoek, Namibia',
        website: 'https://moe.gov.na'
      }
    },
    {
      id: 'MOHSS',
      name: 'Ministry of Health & Social Services',
      permanentSecretary: 'Dr. Ben Nangombe',
      minister: 'Hon. Dr. Kalumbi Shangula',
      overallScore: 68,
      status: 'At Risk',
      complianceAreas: {
        procurement: {
          score: 75,
          status: 'On Track',
          issues: [
            'Award publication compliance: 82%',
            '3 non-compliance notices (2 resolved)',
            'Annual Procurement Plan submitted 5 days late'
          ],
          kpis: [
            { metric: 'Annual Plan Submitted', target: 'Yes', actual: 'Yes (Late)', status: 'At Risk' },
            { metric: 'Award Publication (7 days)', target: 100, actual: 82, unit: '%', status: 'On Track' },
            { metric: 'PPU Non-Compliance Notices', target: 0, actual: 1, unit: 'notices', status: 'At Risk' },
            { metric: 'Funds Certification (14 days)', target: 100, actual: 88, unit: '%', status: 'On Track' }
          ],
          lastAudit: '2024-12-20',
          nextDeadline: '2025-03-01',
          responsibleOfficer: 'Chief Procurement Officer - A. Iiyambo'
        },
        ndp6Reporting: {
          score: 68,
          status: 'At Risk',
          issues: [
            'Q4 2024 report submitted 3 days late',
            'Pillar 2 (Social Transformation) progress: 68%',
            'Budget execution at 68%',
            '5 projects flagged as "At Risk"'
          ],
          kpis: [
            { metric: 'Quarterly Reports On Time', target: 100, actual: 75, unit: '%', status: 'At Risk' },
            { metric: 'NDP6 Pillar Progress', target: 70, actual: 68, unit: '%', status: 'At Risk' },
            { metric: 'Budget Execution', target: 70, actual: 68, unit: '%', status: 'At Risk' },
            { metric: 'Projects On Track', target: 90, actual: 78, unit: '%', status: 'At Risk' }
          ],
          lastReport: '2024-12-23',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of Planning - H. Ndeikwila'
        },
        auditCompliance: {
          score: 54,
          status: 'Critical',
          issues: [
            '18 outstanding AG recommendations from FY 2022/23',
            'Implementation rate: 54%',
            'Financial irregularities totaling N$2.8M unresolved',
            'Quarterly audit committee meetings irregular'
          ],
          kpis: [
            { metric: 'AG Recommendations Implemented', target: 80, actual: 54, unit: '%', status: 'Critical' },
            { metric: 'Outstanding Queries', target: 0, actual: 18, unit: 'items', status: 'Critical' },
            { metric: 'Financial Irregularities Resolved', target: 100, actual: 42, unit: '%', status: 'Critical' },
            { metric: 'Audit Committee Meetings', target: 4, actual: 3, unit: 'meetings', status: 'At Risk' }
          ],
          lastAudit: '2024-11-30',
          nextDeadline: '2025-02-28',
          responsibleOfficer: 'Chief Internal Auditor - M. Shikongo'
        },
        budgetExecution: {
          score: 68,
          status: 'At Risk',
          issues: [
            'Budget execution at 68% (target: 70%)',
            'Capital budget execution: 55%',
            '6 virements pending approval',
            'Monthly expenditure reports submitted on time'
          ],
          kpis: [
            { metric: 'Budget Execution Rate', target: 70, actual: 68, unit: '%', status: 'At Risk' },
            { metric: 'Capital Budget Execution', target: 60, actual: 55, unit: '%', status: 'At Risk' },
            { metric: 'Virements Processed (30 days)', target: 100, actual: 78, unit: '%', status: 'At Risk' },
            { metric: 'Monthly Reports On Time', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReport: '2025-01-09',
          nextDeadline: '2025-02-09',
          responsibleOfficer: 'Chief Accountant - S. Hamunyela'
        },
        performanceManagement: {
          score: 72,
          status: 'On Track',
          issues: [
            'Ministerial Performance Agreement: 72% KPIs achieved',
            'Service delivery standards: 78% compliance',
            'Staff vacancy rate: 32% (critical)',
            'Performance reviews completed on time'
          ],
          kpis: [
            { metric: 'Ministerial KPIs Achieved', target: 80, actual: 72, unit: '%', status: 'At Risk' },
            { metric: 'Service Delivery Compliance', target: 85, actual: 78, unit: '%', status: 'At Risk' },
            { metric: 'Staff Vacancy Rate', target: 15, actual: 32, unit: '%', status: 'Critical' },
            { metric: 'Performance Reviews On Time', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReview: '2024-12-31',
          nextDeadline: '2025-03-31',
          responsibleOfficer: 'Director of HR - E. Shipanga'
        },
        parliamentaryAffairs: {
          score: 78,
          status: 'On Track',
          issues: [
            '8 parliamentary questions pending',
            'Response rate: 78% within 30 days',
            '1 petition unresolved (60 days)',
            'Monthly liaison reports submitted on time'
          ],
          kpis: [
            { metric: 'Questions Answered (30 days)', target: 100, actual: 78, unit: '%', status: 'At Risk' },
            { metric: 'Pending Questions', target: 0, actual: 8, unit: 'questions', status: 'At Risk' },
            { metric: 'Petitions Resolved (90 days)', target: 100, actual: 90, unit: '%', status: 'On Track' },
            { metric: 'Monthly Liaison Reports', target: 100, actual: 100, unit: '%', status: 'On Track' }
          ],
          lastReport: '2025-01-21',
          nextDeadline: '2025-02-21',
          responsibleOfficer: 'Parliamentary Liaison Officer - L. Nauyoma'
        }
      },
      escalations: [
        {
          id: 'ESC-MOHSS-001',
          date: '2025-01-15T14:00:00Z',
          type: 'Audit Compliance',
          severity: 'Critical',
          description: '18 outstanding AG recommendations with only 54% implementation rate',
          status: 'Active',
          initiatedBy: 'Office of the Auditor General',
          assignedTo: 'Permanent Secretary'
        },
        {
          id: 'ESC-MOHSS-002',
          date: '2025-01-12T10:00:00Z',
          type: 'Human Resources',
          severity: 'Critical',
          description: 'Staff vacancy rate at 32% - critical impact on service delivery',
          status: 'Under Review',
          initiatedBy: 'Public Service Commission',
          assignedTo: 'Director of HR'
        }
      ],
      recentActivity: [
        {
          date: '2025-01-21T16:30:00Z',
          action: 'Parliamentary Questions Status Report submitted',
          officer: 'L. Nauyoma',
          category: 'Parliamentary Affairs'
        },
        {
          date: '2025-01-15T14:00:00Z',
          action: 'Escalation raised: Audit compliance critical',
          officer: 'Office of the Auditor General',
          category: 'Audit Compliance'
        },
        {
          date: '2025-01-12T10:00:00Z',
          action: 'Escalation raised: Critical staff vacancy rate',
          officer: 'Public Service Commission',
          category: 'Performance Management'
        },
        {
          date: '2025-01-09T09:00:00Z',
          action: 'Monthly budget execution report submitted (on time)',
          officer: 'S. Hamunyela',
          category: 'Budget'
        },
        {
          date: '2024-12-23T15:00:00Z',
          action: 'NDP6 Q4 2024 report submitted (3 days late)',
          officer: 'H. Ndeikwila',
          category: 'NDP6 Reporting'
        }
      ],
      contactInfo: {
        phone: '+264 61 203 2111',
        email: 'info@mhss.gov.na',
        address: 'Private Bag 13198, Windhoek, Namibia',
        website: 'https://mhss.gov.na'
      }
    }
  ];

  const { filteredMinistries, stats } = useMemo(() => {
    let filtered = ministryComplianceData;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(m => m.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.id.toLowerCase().includes(query) ||
        m.minister.toLowerCase().includes(query) ||
        m.permanentSecretary.toLowerCase().includes(query)
      );
    }

    // Sort by score (lowest first to prioritize problem areas)
    filtered.sort((a, b) => a.overallScore - b.overallScore);

    const stats = {
      total: ministryComplianceData.length,
      onTrack: ministryComplianceData.filter(m => m.status === 'On Track').length,
      atRisk: ministryComplianceData.filter(m => m.status === 'At Risk').length,
      critical: ministryComplianceData.filter(m => m.status === 'Critical').length,
      avgScore: Math.round(ministryComplianceData.reduce((sum, m) => sum + m.overallScore, 0) / ministryComplianceData.length),
      totalEscalations: ministryComplianceData.reduce((sum, m) => sum + m.escalations.length, 0)
    };

    return { filteredMinistries: filtered, stats };
  }, [searchQuery, filterStatus, ministryComplianceData]);

  // Cabinet Decision Compliance — derived from data.cabinetDirectives and data.cabinetAssignments
  const cabinetComplianceByMinistry = useMemo(() => {
    const directives = data?.cabinetDirectives;
    const assignments = data?.cabinetAssignments;
    if (!directives?.length && !assignments?.length) return { ministries: [], overall: null };

    // Build per-ministry stats from directives
    const ministryMap = {};

    (directives || []).forEach(dir => {
      const code = dir?.assignedEntityId;
      const name = dir?.assignedEntityName;
      if (!code || !name) return;
      if (!ministryMap[code]) {
        ministryMap[code] = { code, name, totalDirectives: 0, completedDirectives: 0, overdueDirectives: 0, totalAssignments: 0, completedAssignments: 0, overdueAssignments: 0 };
      }
      ministryMap[code].totalDirectives += 1;
      if (dir?.status === 'Completed') ministryMap[code].completedDirectives += 1;
      if (dir?.status === 'Overdue') ministryMap[code].overdueDirectives += 1;
    });

    // Enrich with assignment-level stats
    (assignments || []).forEach(asg => {
      const code = asg?.assignedTo?.ministry;
      if (!code) return;
      if (!ministryMap[code]) {
        // Assignment exists for a ministry that has no directives assigned directly — use code as name
        ministryMap[code] = { code, name: code, totalDirectives: 0, completedDirectives: 0, overdueDirectives: 0, totalAssignments: 0, completedAssignments: 0, overdueAssignments: 0 };
      }
      ministryMap[code].totalAssignments += 1;
      if (asg?.status === 'Completed') ministryMap[code].completedAssignments += 1;
      if (asg?.status === 'Overdue') ministryMap[code].overdueAssignments += 1;
    });

    // Compute compliance rate and sort lowest first
    const ministries = Object.values(ministryMap)
      .map(m => {
        const total = m.totalDirectives + m.totalAssignments;
        const completed = m.completedDirectives + m.completedAssignments;
        const overdue = m.overdueDirectives + m.overdueAssignments;
        const complianceRate = total > 0 ? Math.round((completed / total) * 100) : 0;
        return { ...m, total, completed, overdue, complianceRate };
      })
      .sort((a, b) => a.complianceRate - b.complianceRate);

    // Overall summary
    const overallTotal = ministries.reduce((s, m) => s + m.total, 0);
    const overallCompleted = ministries.reduce((s, m) => s + m.completed, 0);
    const overallOverdue = ministries.reduce((s, m) => s + m.overdue, 0);
    const overallRate = overallTotal > 0 ? Math.round((overallCompleted / overallTotal) * 100) : 0;

    return {
      ministries,
      overall: { total: overallTotal, completed: overallCompleted, overdue: overallOverdue, complianceRate: overallRate }
    };
  }, [data?.cabinetDirectives, data?.cabinetAssignments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track': return 'green';
      case 'At Risk': return 'amber';
      case 'Critical': return 'red';
      default: return 'slate';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ministry Compliance Dashboard</h1>
          <p className="text-slate-500">
            Comprehensive compliance monitoring across all government ministries
          </p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Export Report
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiRefreshCw} />
            Refresh Data
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="text-center p-4">
          <div className="text-3xl font-black text-slate-900">{stats.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Ministries
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{stats.onTrack}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">On Track</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-amber-500">
          <div className="text-3xl font-black text-amber-700">{stats.atRisk}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">At Risk</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-red-600">
          <div className="text-3xl font-black text-red-700">{stats.critical}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Critical</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-grn-navy">{stats.avgScore}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Avg Score</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-orange-500">
          <div className="text-3xl font-black text-orange-700">{stats.totalEscalations}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Escalations</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Filter by Status
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'Critical', 'At Risk', 'On Track'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap',
                    filterStatus === status
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  {status === 'all' ? 'All Ministries' : status}
                  {status !== 'all' && (
                    <span className={clsx(
                      'ml-2 px-2 py-0.5 rounded-full text-xs font-bold',
                      filterStatus === status ? 'bg-white/20' : 'bg-slate-100'
                    )}>
                      {status === 'Critical' ? stats.critical : status === 'At Risk' ? stats.atRisk : stats.onTrack}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4">
            <div className="relative">
              <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by ministry name, code, minister, or permanent secretary..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
              />
            </div>
          </div>
        </div>
      </Card>

      {!selectedMinistry ? (
        <>
          {/* Ministry Ranking Chart */}
          <Card title="Ministry Compliance Ranking">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={filteredMinistries} layout="vertical">
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis type="category" dataKey="id" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="overallScore" radius={[0, 4, 4, 0]}>
                    {filteredMinistries.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.overallScore >= 80 ? '#009543' : entry.overallScore >= 60 ? '#C4953B' : '#D21034'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Ministry Cards */}
          <div className="space-y-4">
            {filteredMinistries.map(ministry => (
              <Card
                key={ministry.id}
                className="hover:shadow-lg transition-all cursor-pointer"
                onClick={() => setSelectedMinistry(ministry)}
              >
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded">
                          {ministry.id}
                        </span>
                        <Badge status={ministry.status} size="sm" />
                        {ministry.escalations.length > 0 && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded flex items-center gap-1">
                            <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-xs" />
                            {ministry.escalations.length} Escalation{ministry.escalations.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">{ministry.name}</h3>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiUser} />
                          <span>Minister: {ministry.minister}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiBriefcase} />
                          <span>PS: {ministry.permanentSecretary}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-center ml-6">
                      <div className={clsx('text-4xl font-black mb-1', getScoreColor(ministry.overallScore))}>
                        {ministry.overallScore}%
                      </div>
                      <div className="text-xs text-slate-500 uppercase tracking-wider">
                        Overall Score
                      </div>
                      <div className="w-24 bg-slate-200 rounded-full h-2 mt-2">
                        <div
                          className={clsx('h-2 rounded-full', getScoreBgColor(ministry.overallScore))}
                          style={{ width: `${ministry.overallScore}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Areas Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
                    {Object.entries(ministry.complianceAreas).map(([key, area]) => (
                      <div key={key} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-slate-600">
                            {key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase())}
                          </span>
                          <span className={clsx(
                            'text-sm font-bold',
                            area.score >= 80 ? 'text-green-600' : area.score >= 60 ? 'text-amber-600' : 'text-red-600'
                          )}>
                            {area.score}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-1.5">
                          <div
                            className={clsx(
                              'h-1.5 rounded-full',
                              area.score >= 80 ? 'bg-green-500' : area.score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                            )}
                            style={{ width: `${area.score}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* View Details Button */}
                  <div className="pt-4 border-t border-slate-100">
                    <button
                      onClick={() => setSelectedMinistry(ministry)}
                      className="w-full px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiIcons.FiEye} />
                      View Detailed Compliance Report
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Cabinet Decision Compliance */}
          {cabinetComplianceByMinistry?.ministries?.length > 0 && (
            <Card>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiBookOpen} className="text-grn-gold text-lg" />
                    <h3 className="text-lg font-bold text-slate-900">Cabinet Decision Compliance</h3>
                  </div>
                  {cabinetComplianceByMinistry?.overall && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 uppercase tracking-wider">Overall</span>
                      <span className={clsx(
                        'text-xl font-black',
                        cabinetComplianceByMinistry.overall.complianceRate >= 80 ? 'text-green-600'
                          : cabinetComplianceByMinistry.overall.complianceRate >= 60 ? 'text-amber-600'
                          : 'text-red-600'
                      )}>
                        {cabinetComplianceByMinistry.overall.complianceRate}%
                      </span>
                      <span className="text-xs text-slate-500">
                        ({cabinetComplianceByMinistry.overall.completed}/{cabinetComplianceByMinistry.overall.total} items)
                      </span>
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-500">
                  Compliance rate per ministry based on completed directives and assignments. Sorted lowest-first to highlight areas needing attention.
                </p>

                {/* Overall progress bar */}
                {cabinetComplianceByMinistry?.overall && (
                  <div className="w-full bg-slate-200 rounded-full h-2.5">
                    <div
                      className={clsx(
                        'h-2.5 rounded-full transition-all',
                        cabinetComplianceByMinistry.overall.complianceRate >= 80 ? 'bg-green-500'
                          : cabinetComplianceByMinistry.overall.complianceRate >= 60 ? 'bg-amber-500'
                          : 'bg-red-500'
                      )}
                      style={{ width: `${cabinetComplianceByMinistry.overall.complianceRate}%` }}
                    />
                  </div>
                )}

                {/* Per-ministry rows */}
                <div className="divide-y divide-slate-100">
                  {cabinetComplianceByMinistry.ministries.map(ministry => (
                    <div key={ministry.code} className="py-3 first:pt-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded">
                              {ministry.code}
                            </span>
                            <span className="text-sm font-medium text-slate-900 truncate">{ministry.name}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-4 shrink-0">
                          <div className="text-right">
                            <span className={clsx(
                              'text-sm font-bold',
                              ministry.complianceRate >= 80 ? 'text-green-600'
                                : ministry.complianceRate >= 60 ? 'text-amber-600'
                                : 'text-red-600'
                            )}>
                              {ministry.complianceRate}%
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 text-right w-20">
                            {ministry.completed}/{ministry.total} done
                          </div>
                          {ministry.overdue > 0 && (
                            <Badge status="Critical" size="sm">
                              <span className="flex items-center gap-1">
                                <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-xs" />
                                {ministry.overdue} overdue
                              </span>
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5">
                        <div
                          className={clsx(
                            'h-1.5 rounded-full transition-all',
                            ministry.complianceRate >= 80 ? 'bg-green-500'
                              : ministry.complianceRate >= 60 ? 'bg-amber-500'
                              : 'bg-red-500'
                          )}
                          style={{ width: `${ministry.complianceRate}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      ) : (
        /* Detailed Ministry View */
        <div className="space-y-6">
          {/* Back Button */}
          <button
            onClick={() => setSelectedMinistry(null)}
            className="flex items-center gap-2 text-sm text-grn-navy hover:underline"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} />
            Back to All Ministries
          </button>

          {/* Ministry Header */}
          <Card className="bg-gradient-to-r from-grn-navy to-blue-900 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-white/20 text-white text-xs font-bold rounded">
                    {selectedMinistry.id}
                  </span>
                  <span className={clsx(
                    'px-3 py-1 rounded text-xs font-bold',
                    selectedMinistry.status === 'On Track' ? 'bg-green-500 text-white' :
                    selectedMinistry.status === 'At Risk' ? 'bg-amber-500 text-white' :
                    'bg-red-500 text-white'
                  )}>
                    {selectedMinistry.status}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{selectedMinistry.name}</h2>
                <div className="flex items-center gap-4 text-sm opacity-90">
                  <div>
                    <span className="opacity-80">Minister:</span>
                    <span className="font-bold ml-2">{selectedMinistry.minister}</span>
                  </div>
                  <span>•</span>
                  <div>
                    <span className="opacity-80">Permanent Secretary:</span>
                    <span className="font-bold ml-2">{selectedMinistry.permanentSecretary}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-5xl font-black mb-1">{selectedMinistry.overallScore}%</div>
                <div className="text-sm opacity-80">Overall Compliance Score</div>
              </div>
            </div>
          </Card>

          {/* Tabs */}
          <Card>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['overview', 'escalations', 'activity', 'contact'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={clsx(
                    'px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                    activeTab === tab
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </Card>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Compliance Areas Detailed */}
              <div className="space-y-6">
                {Object.entries(selectedMinistry.complianceAreas).map(([key, area]) => (
                  <Card key={key}>
                    <div className="space-y-4">
                      {/* Area Header */}
                      <div className="flex items-start justify-between pb-4 border-b border-slate-100">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-slate-900 mb-1">
                            {key.replace(/([A-Z])/g, ' $1').trim().replace(/^./, str => str.toUpperCase())}
                          </h3>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <SafeIcon icon={FiIcons.FiUser} />
                              <span>{area.responsibleOfficer}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <SafeIcon icon={FiIcons.FiCalendar} />
                              <span>Last: {format(new Date(area.lastAudit || area.lastReport || area.lastReview), 'dd MMM yyyy')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <SafeIcon icon={FiIcons.FiClock} />
                              <span>Next: {format(new Date(area.nextDeadline), 'dd MMM yyyy')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-center ml-6">
                          <div className={clsx('text-4xl font-black mb-1', getScoreColor(area.score))}>
                            {area.score}%
                          </div>
                          <Badge status={area.status} size="sm" />
                        </div>
                      </div>

                      {/* Issues */}
                      {area.issues && area.issues.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                            Key Issues
                          </h4>
                          <div className="space-y-2">
                            {area.issues.map((issue, idx) => (
                              <div key={idx} className="flex items-start gap-2 p-2 bg-slate-50 rounded">
                                <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 mt-0.5 shrink-0" />
                                <span className="text-sm text-slate-700">{issue}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* KPIs */}
                      <div>
                        <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                          Key Performance Indicators
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {area.kpis.map((kpi, idx) => {
                            const isGood = kpi.status === 'On Track';
                            return (
                              <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex justify-between items-start mb-2">
                                  <span className="text-xs font-medium text-slate-600">{kpi.metric}</span>
                                  <span className={clsx(
                                    'px-2 py-1 rounded text-xs font-bold',
                                    isGood ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  )}>
                                    {kpi.status}
                                  </span>
                                </div>
                                <div className="text-sm text-slate-800">
                                  <strong>Actual:</strong> {typeof kpi.actual === 'boolean' ? (kpi.actual ? 'Yes' : 'No') : kpi.actual}
                                  {kpi.unit && ` ${kpi.unit}`}
                                  {' '}<span className="text-slate-500">/ Target: {kpi.target}{kpi.unit && ` ${kpi.unit}`}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}

          {activeTab === 'escalations' && (
            <Card title="Active Escalations">
              {selectedMinistry.escalations.length > 0 ? (
                <div className="space-y-4">
                  {selectedMinistry.escalations.map(escalation => (
                    <div
                      key={escalation.id}
                      className={clsx(
                        'p-4 rounded-lg border',
                        escalation.severity === 'Critical' ? 'bg-red-50 border-red-200' :
                        escalation.severity === 'High' ? 'bg-orange-50 border-orange-200' :
                        'bg-amber-50 border-amber-200'
                      )}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-slate-900 text-white text-xs font-bold rounded font-mono">
                              {escalation.id}
                            </span>
                            <span className={clsx(
                              'px-2 py-1 rounded text-xs font-bold',
                              escalation.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                              escalation.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                              'bg-amber-100 text-amber-700'
                            )}>
                              {escalation.severity}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              {escalation.status}
                            </span>
                          </div>
                          <h4 className="text-sm font-bold text-slate-900 mb-1">{escalation.type}</h4>
                          <p className="text-sm text-slate-700 mb-3">{escalation.description}</p>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <span className="text-slate-500 font-medium">Initiated By:</span>
                              <p className="text-slate-800">{escalation.initiatedBy}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 font-medium">Assigned To:</span>
                              <p className="text-slate-800">{escalation.assignedTo}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 font-medium">Date Raised:</span>
                              <p className="text-slate-800">{format(new Date(escalation.date), 'dd MMM yyyy, HH:mm')}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-3xl mb-2" />
                  <p className="text-sm">No active escalations</p>
                </div>
              )}
            </Card>
          )}

          {activeTab === 'activity' && (
            <Card title="Recent Activity Log">
              <div className="space-y-3">
                {selectedMinistry.recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <SafeIcon icon={FiIcons.FiActivity} className="text-grn-navy mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{activity.action}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{format(new Date(activity.date), 'dd MMM yyyy, HH:mm')}</span>
                        <span>•</span>
                        <span>{activity.officer}</span>
                        <span>•</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                          {activity.category}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {activeTab === 'contact' && (
            <Card title="Ministry Contact Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                    Contact Details
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiIcons.FiPhone} className="text-grn-navy" />
                      <div>
                        <p className="text-xs text-slate-500">Phone</p>
                        <p className="text-sm font-medium text-slate-800">{selectedMinistry.contactInfo.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiIcons.FiMail} className="text-grn-navy" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="text-sm font-medium text-slate-800">{selectedMinistry.contactInfo.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiIcons.FiGlobe} className="text-grn-navy" />
                      <div>
                        <p className="text-xs text-slate-500">Website</p>
                        <a href={selectedMinistry.contactInfo.website} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-grn-navy hover:underline">
                          {selectedMinistry.contactInfo.website}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <SafeIcon icon={FiIcons.FiMapPin} className="text-grn-navy mt-1" />
                      <div>
                        <p className="text-xs text-slate-500">Address</p>
                        <p className="text-sm font-medium text-slate-800">{selectedMinistry.contactInfo.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">
                    Leadership
                  </h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Minister</p>
                      <p className="text-sm font-bold text-slate-900">{selectedMinistry.minister}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-500 mb-1">Permanent Secretary</p>
                      <p className="text-sm font-bold text-slate-900">{selectedMinistry.permanentSecretary}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Legal Framework Notice */}
      <Card className="bg-grn-navy text-white">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-xl shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-bold mb-2">Ministry Compliance Monitoring Framework</p>
            <p className="opacity-90">
              This dashboard tracks ministerial compliance across six key areas: <strong>Procurement</strong> (Public Procurement Act 2015),{' '}
              <strong>NDP6 Reporting</strong> (NDP6 IMEP Framework), <strong>Audit Compliance</strong> (Audit Act 2005),{' '}
              <strong>Budget Execution</strong> (State Finance Act 1991), <strong>Performance Management</strong> (Public Service Act 1995),{' '}
              and <strong>Parliamentary Affairs</strong> (Constitution of Namibia). All data is updated in real-time and subject to{' '}
              Prime Minister's Office oversight and Cabinet review.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};